(() => {
  'use strict';

  const ELEMENT_NAME = 'ev-rebate-finder';

  if (customElements.get(ELEMENT_NAME)) return;

  const ZIP_PATTERN = /^\d{5}$/;
  const CACHE_VERSION = 'eviqo-v4-observed-2026-07-31';
  const CACHE_PREFIX = 'autel-ev-rebate-finder:';
  const CACHE_TTL_MS = 24 * 60 * 60 * 1000;
  const NLR_API_KEY = 'ACTeLuJa92wVKUhBkq756iFQbvUqt0cQ8Defg6gB';
  const NLR_API_URL = 'https://developer.nlr.gov/api/transportation-incentives-laws/v1.json';
  const ZIP_API_URL = 'https://api.zippopotam.us/us/';
  const POST_30C_CUTOFF = Date.parse('2026-07-01T00:00:00Z');

  const ACCEPT_KEYWORDS = [
    'ev charger',
    'evse',
    'electric vehicle supply equipment',
    'level 2',
    'home charger',
    'residential charging',
    'charging equipment',
    'charging station',
    'refueling property',
    'alternative fuel infrastructure',
    'charging infrastructure',
    'home charging',
    'electric vehicle charging'
  ];

  const REJECT_KEYWORDS = [
    'fleet',
    'school bus',
    'transit bus',
    'medium-duty',
    'heavy-duty',
    'class 4',
    'class 5',
    'class 6',
    'class 7',
    'class 8',
    'hydrogen',
    'natural gas',
    'propane',
    'biodiesel',
    'ethanol',
    'aircraft',
    'aviation',
    'marine',
    'forklift',
    'nevi',
    'national electric vehicle infrastructure',
    'rural',
    'large-scale',
    'public housing',
    'rental assistance',
    'rad program',
    'choice neighborhoods',
    'transportation facility',
    'federal lands',
    'business loan',
    'intermediary',
    'rural utility',
    'neighborhood revitalization',
    'public-private partnership',
    'workplace charging',
    'workplace charger',
    'multi-unit dwelling',
    'multi-family',
    'multifamily',
    'condominium',
    'community charging',
    'department of agriculture',
    'usda',
    'department of housing',
    'urban development',
    'enterprise grant',
    'business development grant',
    'rural energy savings',
    'federal highway administration',
    'rural economic',
    'community facility',
    'rural development',
    'commercial property',
    'depreciable property',
    'for-profit'
  ];

  const RESIDENTIAL_KEYWORDS = [
    'residential',
    'home charger',
    'home charging',
    'home installation',
    'single-family',
    'single family',
    'single family home',
    'homeowner',
    'principal residence',
    'primary residence',
    'private residence',
    'qualified consumer',
    'individual taxpayer'
  ];

  class RebateLookupError extends Error {
    constructor(code, message, cause) {
      super(message);
      this.name = 'RebateLookupError';
      this.code = code;
      this.cause = cause;
    }
  }

  class DirectAfdcProvider {
    async lookup(zip, options = {}) {
      const location = await this.lookupLocation(zip, options.signal);
      const programLookup = await this.lookupPrograms(location.stateCode, options.signal);

      return {
        zip,
        location,
        records: programLookup.records,
        complete: programLookup.complete,
        retrievedAt: new Date().toISOString()
      };
    }

    async lookupLocation(zip, signal) {
      const data = await this.requestJson(`${ZIP_API_URL}${encodeURIComponent(zip)}`, {
        signal,
        service: 'zip'
      });
      const place = data && Array.isArray(data.places) ? data.places[0] : null;
      const stateCode = place && place['state abbreviation'];
      const stateName = place && place.state;

      if (!stateCode || !stateName) {
        throw new RebateLookupError('zip_not_found', 'We could not match that ZIP Code to a U.S. state.');
      }

      return { stateCode, stateName };
    }

    async lookupPrograms(stateCode, signal) {
      const stateParams = new URLSearchParams({
        api_key: NLR_API_KEY,
        jurisdiction: stateCode,
        law_type: 'STATEINC,UPINC'
      });
      const federalParams = new URLSearchParams({
        api_key: NLR_API_KEY,
        jurisdiction: 'US',
        law_type: 'INC,LAWREG'
      });

      const responses = await Promise.allSettled([
        this.requestJson(`${NLR_API_URL}?${stateParams}`, { signal, service: 'afdc' }),
        this.requestJson(`${NLR_API_URL}?${federalParams}`, { signal, service: 'afdc' })
      ]);
      const failures = responses
        .filter((result) => result.status === 'rejected')
        .map((result) => result.reason);
      const aborted = failures.find((error) => error && error.name === 'AbortError');

      if (aborted) throw aborted;

      const successes = responses.filter((result) => result.status === 'fulfilled');

      if (!successes.length) throw failures[0];

      return {
        complete: successes.length === responses.length,
        records: successes.flatMap(({ value: data }) => {
          if (Array.isArray(data.result)) return data.result;
          if (Array.isArray(data.results)) return data.results;
          return [];
        })
      };
    }

    async requestJson(url, { signal, service }) {
      let response;

      try {
        response = await fetch(url, {
          method: 'GET',
          headers: { Accept: 'application/json' },
          signal
        });
      } catch (error) {
        if (error && error.name === 'AbortError') throw error;
        throw new RebateLookupError('network', 'The rebate service could not be reached.', error);
      }

      if (response.status === 404 && service === 'zip') {
        throw new RebateLookupError('zip_not_found', 'We could not match that ZIP Code to a U.S. state.');
      }
      if (response.status === 429) {
        throw new RebateLookupError(
          'rate_limited',
          'The rebate database has reached its temporary request limit.'
        );
      }
      if (response.status === 401 || response.status === 403) {
        throw new RebateLookupError(
          'provider_auth',
          'The rebate database is temporarily unavailable for this lookup.'
        );
      }
      if (!response.ok) {
        throw new RebateLookupError(
          'provider',
          `The ${service === 'zip' ? 'ZIP lookup' : 'rebate database'} returned an error.`
        );
      }

      try {
        return await response.json();
      } catch (error) {
        throw new RebateLookupError('invalid_response', 'The rebate service returned invalid data.', error);
      }
    }
  }

  function filterPrograms(records, now = Date.now()) {
    if (!Array.isArray(records)) return [];

    return records
      .filter((record) => {
        const status = String(record.status || '').toLowerCase();

        if (status === 'expired' || status === 'repealed' || status === 'archived') return false;

        if (record.expired_date) {
          const expiration = Date.parse(record.expired_date);
          if (!Number.isNaN(expiration) && expiration < now) return false;
        }

        const technologyBlob = [
          record.technology_categories,
          record.technologies,
          record.fuel_types,
          record.technology
        ]
          .filter(Boolean)
          .join(' ')
          .toUpperCase();

        if (technologyBlob && !technologyBlob.includes('ELEC')) return false;

        const content = `${record.title || ''} ${record.text || ''}`.toLowerCase();

        if (
          record.state === 'US' &&
          /8911|refueling property|section 30c|30c\b/.test(content) &&
          now > POST_30C_CUTOFF
        ) {
          return false;
        }

        if (REJECT_KEYWORDS.some((keyword) => content.includes(keyword))) return false;
        if (!ACCEPT_KEYWORDS.some((keyword) => content.includes(keyword))) return false;
        if (content.includes('this page provides a summary')) return false;

        const sourceType = String(record.type || '').toLowerCase();
        const isFederal = record.state === 'US';
        const isUtility = sourceType.includes('utility');

        if (
          !isFederal &&
          !isUtility &&
          !RESIDENTIAL_KEYWORDS.some((keyword) => content.includes(keyword))
        ) {
          return false;
        }

        return true;
      })
      .map(normalizeProgram)
      .sort((left, right) => Number(left.tag === 'Federal') - Number(right.tag === 'Federal'));
  }

  function normalizeProgram(record) {
    const text = `${record.title || ''} ${record.text || ''}`;
    let tag = 'State';

    if (record.state === 'US') {
      tag = 'Federal';
    } else if (String(record.type || '').toLowerCase().includes('utility')) {
      tag = 'Utility';
    }

    return {
      id: String(record.id || ''),
      name: String(record.title || 'EV Charger Incentive').trim(),
      summary: shortenText(record.text || '', 240),
      amount: extractAmount(text),
      tag,
      sourceUrl: `https://afdc.energy.gov/laws/${encodeURIComponent(record.id || '')}`
    };
  }

  function extractAmount(text) {
    if (!text) return '';

    const patterns = [
      /up to \$([\d,]+(?:\.\d+)? ?(?:million|billion)?)/i,
      /\$([\d,]+(?:\.\d+)? ?(?:million|billion)?) (rebate|incentive|credit|grant|per)/i,
      /([\d]{1,2}(?:\.\d+)?)% (of|tax credit)/i
    ];

    for (const pattern of patterns) {
      const match = String(text).match(pattern);
      if (!match) continue;
      if (match[0].includes('%')) return `${match[1]}%`;

      const amount = match[1].trim();
      const numericAmount = Number.parseInt(amount.replace(/,/g, ''), 10);

      if (!Number.isNaN(numericAmount) && numericAmount < 50 && !/million|billion/i.test(amount)) {
        continue;
      }

      return `$${amount}`;
    }

    return '';
  }

  function shortenText(text, maximumLength) {
    if (!text) return '';

    let cleanText = String(text)
      .replace(/<[^>]+>/g, ' ')
      .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      .replace(/&nbsp;/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();

    if (cleanText.length <= maximumLength) return cleanText;

    const slice = cleanText.slice(0, maximumLength);
    const lastSentence = slice.lastIndexOf('. ');

    if (lastSentence > maximumLength / 2) {
      return slice.slice(0, lastSentence + 1);
    }

    return `${slice.trim()}…`;
  }

  function readCache(zip) {
    try {
      const raw = window.localStorage.getItem(`${CACHE_PREFIX}${CACHE_VERSION}:${zip}`);
      if (!raw) return null;

      const entry = JSON.parse(raw);

      if (
        entry.version !== CACHE_VERSION ||
        !Number.isFinite(entry.expiresAt) ||
        entry.expiresAt <= Date.now() ||
        !entry.data
      ) {
        window.localStorage.removeItem(`${CACHE_PREFIX}${CACHE_VERSION}:${zip}`);
        return null;
      }

      return sanitizeCachedData(entry.data);
    } catch (_error) {
      return null;
    }
  }

  function writeCache(zip, data) {
    try {
      window.localStorage.setItem(
        `${CACHE_PREFIX}${CACHE_VERSION}:${zip}`,
        JSON.stringify({
          version: CACHE_VERSION,
          expiresAt: Date.now() + CACHE_TTL_MS,
          data
        })
      );
    } catch (_error) {
      // Storage can be unavailable in private browsing or blocked contexts.
    }
  }

  function sanitizeCachedData(data) {
    const location = data && data.location;
    const programs = data && data.programs;

    if (
      !location ||
      !/^[A-Z]{2}$/.test(String(location.stateCode || '')) ||
      !location.stateName ||
      !Array.isArray(programs)
    ) {
      return null;
    }

    return {
      zip: String(data.zip || ''),
      location: {
        stateCode: String(location.stateCode),
        stateName: String(location.stateName)
      },
      retrievedAt: String(data.retrievedAt || ''),
      programs: programs
        .filter((program) => program && program.id && program.name)
        .map((program) => ({
          id: String(program.id),
          name: String(program.name),
          summary: String(program.summary || ''),
          amount: String(program.amount || ''),
          tag: ['Federal', 'Utility', 'State'].includes(program.tag) ? program.tag : 'State',
          sourceUrl: `https://afdc.energy.gov/laws/${encodeURIComponent(program.id)}`
        }))
    };
  }

  function formatRetrievedDate(value) {
    const date = value ? new Date(value) : new Date();
    if (Number.isNaN(date.getTime())) return '';

    try {
      return new Intl.DateTimeFormat(document.documentElement.lang || 'en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      }).format(date);
    } catch (_error) {
      return date.toLocaleDateString('en-US');
    }
  }

  function createElement(tagName, className, text) {
    const element = document.createElement(tagName);
    if (className) element.className = className;
    if (text !== undefined) element.textContent = text;
    return element;
  }

  class EvRebateFinder extends HTMLElement {
    static filterPrograms(records, now) {
      return filterPrograms(records, now);
    }

    connectedCallback() {
      if (this.initialized) return;

      this.entryMode = this.dataset.entryMode === 'popup' ? 'popup' : 'inline';
      this.form = this.querySelector('[data-evrf-form]');
      this.zipInput = this.querySelector('[data-evrf-zip]');
      this.submitButton = this.querySelector('[data-evrf-submit]');
      this.fieldError = this.querySelector('[data-evrf-field-error]');
      this.digitStatus = this.querySelector('[data-evrf-digit-status]');
      this.triggerButton = this.querySelector('[data-evrf-trigger]');
      this.dialog = this.querySelector('[data-evrf-dialog]');
      this.dialogTitle = this.querySelector('[data-evrf-dialog-title]');
      this.resultsHeading = this.querySelector('[data-evrf-results-heading]');
      this.locationName = this.querySelector('[data-evrf-location]');
      this.locationSummary = this.querySelector('[data-evrf-location-summary]');
      this.programsContainer = this.querySelector('[data-evrf-programs]');
      this.retrieved = this.querySelector('[data-evrf-retrieved]');
      this.closeButton = this.querySelector('[data-evrf-close]');
      this.resetButton = this.querySelector('[data-evrf-reset]');
      this.views = Array.from(this.querySelectorAll('[data-evrf-view]'));

      if (!this.form || !this.zipInput || !this.submitButton || !this.dialog) return;

      this.provider = new DirectAfdcProvider();
      this.onSubmit = this.handleSubmit.bind(this);
      this.onInput = this.handleInput.bind(this);
      this.onTrigger = this.openEntryDialog.bind(this);
      this.onClose = this.handleDialogClose.bind(this);
      this.onCloseClick = this.closeDialog.bind(this);
      this.onReset = this.resetLookup.bind(this);
      this.onDialogKeydown = this.handleDialogKeydown.bind(this);

      this.form.addEventListener('submit', this.onSubmit);
      this.zipInput.addEventListener('input', this.onInput);
      this.triggerButton?.addEventListener('click', this.onTrigger);
      this.dialog.addEventListener('close', this.onClose);
      this.dialog.addEventListener('keydown', this.onDialogKeydown);
      this.closeButton?.addEventListener('click', this.onCloseClick);
      this.resetButton?.addEventListener('click', this.onReset);
      this.updateZipState();

      this.initialized = true;
    }

    disconnectedCallback() {
      if (!this.initialized) return;

      this.abortController?.abort();
      this.form?.removeEventListener('submit', this.onSubmit);
      this.zipInput?.removeEventListener('input', this.onInput);
      this.triggerButton?.removeEventListener('click', this.onTrigger);
      this.dialog?.removeEventListener('close', this.onClose);
      this.dialog?.removeEventListener('keydown', this.onDialogKeydown);
      this.closeButton?.removeEventListener('click', this.onCloseClick);
      this.resetButton?.removeEventListener('click', this.onReset);
      this.initialized = false;
    }

    handleInput() {
      this.zipInput.value = this.zipInput.value.replace(/\D/g, '').slice(0, 5);
      this.updateZipState();
      if (this.fieldError.textContent) this.setFieldError('');
    }

    updateZipState() {
      const digitCount = this.zipInput.value.length;
      const isComplete = ZIP_PATTERN.test(this.zipInput.value);

      if (this.digitStatus) {
        this.digitStatus.textContent = `${digitCount} of 5 digits entered`;
      }

      this.zipInput.classList.toggle('is-complete', isComplete);
      this.submitButton.disabled = Boolean(this.pending) || !isComplete;
    }

    openEntryDialog(event) {
      this.openFromExternal(event?.currentTarget || this.triggerButton);
    }

    openFromExternal(trigger) {
      if (this.pending || this.entryMode !== 'popup') return;

      this.externalTrigger = trigger && trigger !== this.triggerButton ? trigger : null;
      this.lastFocused = trigger || document.activeElement;
      this.setFieldError('');
      this.setView('entry');
      this.openDialog();
      window.requestAnimationFrame(() => {
        this.zipInput.focus();
        this.zipInput.select();
      });
    }

    handleSubmit(event) {
      event.preventDefault();
      this.startLookup();
    }

    async startLookup() {
      if (this.pending) return;

      const zip = this.zipInput.value.trim();

      if (!ZIP_PATTERN.test(zip)) {
        this.setFieldError('Enter a valid 5-digit U.S. ZIP code.');
        this.zipInput.focus();
        return;
      }

      this.setFieldError('');

      if (!this.dialog.open) {
        this.lastFocused = document.activeElement;
        this.openDialog();
      }

      this.setView('loading');
      this.setBusy(true);

      const cached = readCache(zip);

      if (cached) {
        this.renderResults(cached, true);
        this.setBusy(false);
        return;
      }

      this.abortController = new AbortController();

      try {
        const response = await this.provider.lookup(zip, {
          signal: this.abortController.signal
        });
        const data = {
          zip,
          location: response.location,
          programs: filterPrograms(response.records),
          retrievedAt: response.retrievedAt
        };

        if (response.complete) {
          writeCache(zip, data);
        }
        this.renderResults(data, false);
      } catch (error) {
        if (error && error.name === 'AbortError') return;
        this.renderError(error);
      } finally {
        this.abortController = null;
        this.setBusy(false);
      }
    }

    setBusy(busy) {
      this.pending = busy;
      this.setAttribute('aria-busy', busy ? 'true' : 'false');
      this.submitButton.classList.toggle('is-loading', busy);
      this.updateZipState();
    }

    setFieldError(message) {
      this.fieldError.textContent = message;
      this.zipInput.setAttribute('aria-invalid', message ? 'true' : 'false');
    }

    setView(name) {
      this.views.forEach((view) => {
        view.hidden = view.dataset.evrfView !== name;
      });
    }

    openDialog() {
      if (this.dialog.open) return;

      if (typeof this.dialog.showModal === 'function') {
        this.dialog.showModal();
      } else {
        this.dialog.setAttribute('open', '');
        this.dialog.classList.add('is-fallback-open');
      }

      this.triggerButton?.setAttribute('aria-expanded', 'true');
      this.externalTrigger?.setAttribute('aria-expanded', 'true');
    }

    closeDialog() {
      this.abortController?.abort();

      if (typeof this.dialog.close === 'function' && this.dialog.open) {
        this.dialog.close();
      } else {
        this.dialog.removeAttribute('open');
        this.dialog.classList.remove('is-fallback-open');
        this.handleDialogClose();
      }
    }

    handleDialogClose() {
      this.abortController?.abort();
      this.setBusy(false);
      this.triggerButton?.setAttribute('aria-expanded', 'false');
      this.externalTrigger?.setAttribute('aria-expanded', 'false');

      const focusTarget =
        this.lastFocused && typeof this.lastFocused.focus === 'function'
          ? this.lastFocused
          : this.zipInput;

      window.requestAnimationFrame(() => focusTarget?.focus());
      this.externalTrigger = null;
    }

    handleDialogKeydown(event) {
      if (event.key === 'Escape' && this.dialog.classList.contains('is-fallback-open')) {
        event.preventDefault();
        this.closeDialog();
      }
    }

    resetLookup() {
      this.abortController?.abort();

      if (this.entryMode === 'popup') {
        this.setBusy(false);
        this.setFieldError('');
        this.setView('entry');
        window.requestAnimationFrame(() => {
          this.zipInput.focus();
          this.zipInput.select();
        });
        return;
      }

      this.lastFocused = this.zipInput;
      this.closeDialog();
      window.requestAnimationFrame(() => {
        this.zipInput.focus();
        this.zipInput.select();
      });
    }

    renderError(error) {
      const messages = {
        zip_not_found: 'ZIP code not found. Check the ZIP and try again.',
        rate_limited: 'We couldn’t complete the search. Please try again later.',
        provider_auth: 'We couldn’t complete the search. Please try again later.',
        invalid_response: 'We couldn’t complete the search. Please try again later.',
        network: 'Check your connection and try again.',
        provider: 'We couldn’t complete the search. Please try again later.'
      };
      const message = messages[error && error.code] || 'We couldn’t complete the search. Please try again later.';

      this.setFieldError(message);

      if (this.entryMode === 'popup') {
        this.setView('entry');
      } else {
        this.lastFocused = this.zipInput;
        this.closeDialog();
      }

      window.requestAnimationFrame(() => {
        this.zipInput.focus();
        this.zipInput.select();
      });
    }

    renderResults(data, fromCache) {
      const count = data.programs.length;

      this.locationName.textContent = `${data.location.stateName} (${data.location.stateCode})`;
      this.locationSummary.textContent = `ZIP ${data.zip} · ${count} related program${count === 1 ? '' : 's'}`;
      this.resultsHeading.textContent = count
        ? `${count} Related Program${count === 1 ? '' : 's'}`
        : '';
      this.programsContainer.replaceChildren();

      if (!count) {
        const emptyState = createElement(
          'div',
          'evrf__empty',
          'No matching programs were found in AFDC for this state. Check with your electric utility for local offers.'
        );
        emptyState.tabIndex = -1;
        this.programsContainer.append(emptyState);
      } else {
        const list = createElement('ul', 'evrf__program-list');
        data.programs.forEach((program) => list.append(this.createProgramCard(program)));
        this.programsContainer.append(list);
      }

      const retrievedDate = formatRetrievedDate(data.retrievedAt);
      this.retrieved.textContent = [
        'U.S. DOE AFDC',
        retrievedDate ? `Retrieved ${retrievedDate}` : '',
        fromCache ? 'Cached' : ''
      ]
        .filter(Boolean)
        .join(' · ');

      this.setView('results');
      window.requestAnimationFrame(() => {
        (count ? this.resultsHeading : this.programsContainer.querySelector('.evrf__empty'))?.focus?.();
      });
    }

    createProgramCard(program) {
      const item = createElement('li', 'evrf__program');
      const tags = createElement('div', 'evrf__program-tags');
      const typeTag = createElement(
        'span',
        `evrf__tag evrf__tag--${program.tag.toLowerCase()}`,
        program.tag
      );

      tags.append(typeTag);

      if (program.amount) {
        tags.append(createElement('span', 'evrf__tag evrf__tag--amount', program.amount));
      }

      const title = createElement('h4', 'evrf__program-name', program.name);
      const summary = createElement('p', 'evrf__program-summary', program.summary);
      const link = createElement('a', 'evrf__program-link');

      link.href = program.sourceUrl;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      link.append(
        document.createTextNode('View details on AFDC '),
        createElement('span', 'evrf__external-icon', '↗')
      );
      link.querySelector('.evrf__external-icon').setAttribute('aria-hidden', 'true');

      item.append(tags, title);
      if (program.summary) item.append(summary);
      item.append(link);

      return item;
    }
  }

  customElements.define(ELEMENT_NAME, EvRebateFinder);

  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;

    const trigger = event.target.closest('[data-evrf-open]');
    if (!trigger) return;

    const finderKey = trigger.dataset.evrfOpen || '';
    const finder = Array.from(document.querySelectorAll(ELEMENT_NAME)).find((candidate) => {
      if (candidate.dataset.entryMode !== 'popup') return false;
      return !finderKey || candidate.dataset.finderKey === finderKey;
    });

    if (!finder || typeof finder.openFromExternal !== 'function') return;

    event.preventDefault();
    if (finder.dialog?.id) trigger.setAttribute('aria-controls', finder.dialog.id);
    finder.openFromExternal(trigger);
  });
})();
